<template>
  <v-row>
    <v-col cols="12">
      <v-card>
        <div
          class="stock-universe-chart"
          ref="chartContainer"
          v-resize="onResize"
        >
          <svg :width="width" :height="height" ref="svg">
            <!-- Defs -->
            <defs>
              <linearGradient
                id="stock-universe-chart-dislike-zone"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stop-color="#FFE6E7"></stop>
                <stop offset="100%" stop-color="#E9507F"></stop>
              </linearGradient>
              <linearGradient
                id="stock-universe-chart-like-zone"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stop-color="#00868A"></stop>
                <stop offset="100%" stop-color="#DAFEFF"></stop>
              </linearGradient>
            </defs>
            <!--  Drop zones -->
            <g class="drop-zones">
              <g
                v-for="(zone, i) in zones"
                :key="zone.zone"
                class="drop-zone"
                :transform="i ? `translate(${width - dropZoneWidth},0)` : null"
              >
                <rect
                  class="drop-zone-rect"
                  :width="dropZoneWidth"
                  :height="height"
                  :fill="`url(#stock-universe-chart-${zone.zone}-zone)`"
                ></rect>
                <text
                  class="drop-zone-label"
                  :font-size="dropZoneLabelSize"
                  font-weight="bold"
                  fill="#233767"
                  :x="dropZoneWidth / 2"
                  y="16"
                  dy="0.8em"
                  text-anchor="middle"
                >
                  {{ zone.zone.toUpperCase() }}
                </text>
                <path
                  v-for="(star, j) in zone.stars"
                  :key="j"
                  class="drop-zone-star"
                  :fill="star ? '#3B98EE' : '#0C2243'"
                  :transform="
                    `translate(${(dropZoneWidth - starSize) / 2},${height / 2 +
                      (j - 2.5) * 52})scale(${starSize / 24})`
                  "
                  d="M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.45,13.97L5.82,21L12,17.27Z"
                />
              </g>
            </g>
            <!-- Stock zone -->
            <g class="stock-zone" ref="stockZone"></g>
          </svg>
          <div class="chart-instruction">
            Move stock to DISLIKE or LIKE to reflect convictions
          </div>
          <v-btn outlined class="reset-button" @click="reset">
            Reset Ratings
          </v-btn>
          <div class="chart-tooltip" ref="tooltip"></div>
          <div class="chart-loader" :class="{ active: isLoading }">
            <div class="lds-ellipsis">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { scaleLinear } from "d3-scale";
import { drag } from "d3-drag";
import { select, event } from "d3-selection";
import "d3-transition";
const getEvent = () => event;
export default {
  props: {
    allStocks: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    tabletBreakpoint: 800,
    mobileBreakpoint: 400,
    width: 960,
    height: 700,
    dropZoneWidth: 140,
    starSize: 48,
    dropZoneLabelSize: 30,
    drag: null,
    zones: [
      { zone: "dislike", stars: [0, 0, 0, 0, 1] },
      { zone: "like", stars: [1, 1, 1, 1, 1] }
    ],
    isTooltipDisabled: false
  }),
  computed: {
    x() {
      return scaleLinear()
        .domain([1, 5])
        .range([this.dropZoneWidth + 40, this.width - this.dropZoneWidth - 40]);
    }
  },
  watch: {
    allStocks: {
      handler(value) {
        value.forEach(stock => {
          if (!stock.y0) {
            // When the stock is first added
            stock.y0 = this.getRandomY();
            stock.y = stock.y0;
          }
        });
        this.updateChart();
      }
    }
  },
  mounted() {
    this.drag = drag()
      .container(this.$refs.svg)
      .on("start", this.onDragStart)
      .on("drag", this.onDragging)
      .on("end", this.onDragEnd);
    this.onResize();
  },
  methods: {
    onResize() {
      this.width = this.$refs.chartContainer.clientWidth;
      if (this.width <= this.mobileBreakpoint) {
        this.dropZoneWidth = 56;
        this.starSize = 24;
        this.dropZoneLabelSize = 12;
      } else if (this.width <= this.tabletBreakpoint) {
        this.dropZoneWidth = 100;
        this.starSize = 48;
        this.dropZoneLabelSize = 16;
      } else {
        this.dropZoneWidth = 140;
        this.starSize = 48;
        this.dropZoneLabelSize = 30;
      }
      this.$nextTick(() => {
        select(this.$refs.stockZone)
          .selectAll(".stock-chip")
          .attr("transform", d => {
            if (d.liked) {
              return `translate(${this.width - this.dropZoneWidth / 2}, ${
                d.y
              })`;
            } else if (d.disliked) {
              return `translate(${this.dropZoneWidth / 2}, ${d.y})`;
            } else {
              return `translate(${this.x(d.rating)}, ${d.y0})`;
            }
          });
      });
    },
    onDragStart(_, i, n) {
      this.isTooltipDisabled = true;
      select(this.$refs.tooltip).style("opacity", 0);
      select(n[i]).style("cursor", "grabbing");
    },
    onDragging(d, i, n) {
      select(n[i])
        .raise()
        .attr(
          "transform",
          `translate(${Math.min(
            Math.max(d.labelDimension.width / 2, getEvent().x),
            this.width - d.labelDimension.width / 2
          )}, ${Math.min(
            Math.max(d.labelDimension.height / 2, getEvent().y),
            this.height - d.labelDimension.height / 2
          )})`
        );
    },
    onDragEnd(d, i, n) {
      const stockChip = select(n[i]).style("cursor", "grab");
      // Determine if it's in the drop zone
      if (getEvent().x < this.dropZoneWidth) {
        // Keep the stock in the zone
        d.y = getEvent().y;
        stockChip
          .transition()
          .attr("transform", `translate(${this.dropZoneWidth / 2}, ${d.y})`);
        // Only request new rating when the stock isn't previously in dislike already
        if (!d.disliked) {
          this.$emit("add-disliked-stock", d.symbol);
        }
      } else if (getEvent().x > this.width - this.dropZoneWidth) {
        // Keep the stock in the zone
        d.y = getEvent().y;
        stockChip
          .transition()
          .attr(
            "transform",
            `translate(${this.width - this.dropZoneWidth / 2}, ${d.y})`
          );
        // Only request new rating when the stock isn't previously in liked already
        if (!d.liked) {
          this.$emit("add-liked-stock", d.symbol);
        }
      } else {
        // Reset the stock back to its starting position
        stockChip
          .transition()
          .attr("transform", `translate(${this.x(d.rating)}, ${d.y0})`);
      }
      this.isTooltipDisabled = false;
    },
    tooltipShow(d, i, n) {
      event.preventDefault();
      if (this.isTooltipDisabled) return;
      const tooltip = select(this.$refs.tooltip);
      const html = d.info.map(e => `<div>${e}</div>`).join("");
      tooltip.html(html);
      // Calculate tooltip dimension
      const {
        width: tooltipWidth,
        height: tooltipHeight
      } = tooltip.node().getBoundingClientRect();
      // Calculate the stock chip dimension
      const {
        top: chipTop,
        left: chipLeft,
        right: chipRight,
        width: chipWidth
      } = n[i].getBoundingClientRect();
      // Calculate the container dimension
      const {
        top: containerTop,
        left: containerLeft,
        width: containerWidth
      } = this.$el.getBoundingClientRect();

      const padding = 4;
      // Try place the tooltip to the right of the chip first
      let left = chipRight + padding - containerLeft;
      let top = chipTop - containerTop;
      if (left + tooltipWidth > containerWidth) {
        // Try place the tooltip to the left of the chip
        left = chipLeft - padding - tooltipWidth - containerLeft;
      }
      if (left < 0) {
        // Place the tooltip above the chip
        left = chipLeft + chipWidth / 2 - tooltipWidth / 2 - containerLeft;
        top = chipTop - padding - tooltipHeight - containerTop;
      }
      tooltip
        .style("transform", `translate(${left}px,${top}px)`)
        .style("opacity", 1);
    },
    tooltipHide() {
      event.preventDefault();
      const tooltip = select(this.$refs.tooltip);
      tooltip.style("opacity", 0);
    },
    reset() {
      this.$emit("reset-ratings");
    },
    getRandomY() {
      return 64 + Math.random() * (this.height - 128);
    },
    updateChart() {
      select(this.$refs.stockZone)
        .selectAll(".stock-chip")
        .data(
          this.allStocks.filter(d => d.rating),
          d => d.symbol
        )
        .join(
          enter =>
            enter
              .append("g")
              .attr("class", "stock-chip")
              .style("cursor", "grab")
              .call(this.drag)
              .call(g =>
                g
                  .append("text")
                  .attr("class", "stock-chip-label")
                  .attr("text-anchor", "middle")
                  .attr("font-weight", "bold")
                  .attr("font-size", 10)
                  .attr("fill", "#3B98EE")
                  .attr("dy", "0.35em")
                  .text(d => d.symbol)
                  .each(function(d) {
                    const { width, height } = this.getBBox();
                    d.labelDimension = {
                      width: width + 12,
                      height: height + 6
                    };
                  })
              )
              .call(g =>
                g
                  .insert("rect", ".stock-chip-label")
                  .attr("x", d => -d.labelDimension.width / 2)
                  .attr("y", d => -d.labelDimension.height / 2)
                  .attr("width", d => d.labelDimension.width)
                  .attr("height", d => d.labelDimension.height)
                  .attr("fill", "#162750")
              )
              .attr("transform", d => `translate(${this.x(d.rating)}, ${d.y0})`)
              .on("mouseenter", this.tooltipShow)
              .on("touchstart", this.tooltipShow, { passive: true })
              .on("mouseleave touchend", this.tooltipHide),
          update => update,
          exit => exit.remove()
        )
        .transition()
        .duration(500)
        .attr("transform", d => {
          if (d.liked) {
            return `translate(${this.width - this.dropZoneWidth / 2}, ${d.y})`;
          } else if (d.disliked) {
            return `translate(${this.dropZoneWidth / 2}, ${d.y})`;
          } else {
            return `translate(${this.x(d.rating)}, ${d.y0})`;
          }
        });
    }
  }
};
</script>

<style scoped>
.stock-universe-chart {
  font-size: 1rem;
  line-height: 1.25;
  position: relative;
}

.stock-universe-chart .chart-instruction {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: 50%;
  text-align: center;
  color: #d9e2ec;
}

.stock-universe-chart svg {
  display: block;
}

.stock-universe-chart .stock-chip-label {
  user-select: none;
}

.stock-universe-chart .reset-button {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
}

/* Tooltip */
.stock-universe-chart .chart-tooltip {
  position: absolute;
  top: 0;
  left: 0;
  background: #363636;
  color: #fff;
  font-size: 0.625rem;
  padding: 0.5em 0.75em;
  pointer-events: none;
  opacity: 0;
}

/* Loader */
.stock-universe-chart .chart-loader {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.5);
  display: none;
}
.stock-universe-chart .chart-loader.active {
  display: flex;
  justify-content: center;
  align-items: center;
}
.lds-ellipsis {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}
.lds-ellipsis div {
  position: absolute;
  top: 33px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #fff;
  animation-timing-function: cubic-bezier(0, 1, 1, 0);
}
.lds-ellipsis div:nth-child(1) {
  left: 8px;
  animation: lds-ellipsis1 0.6s infinite;
}
.lds-ellipsis div:nth-child(2) {
  left: 8px;
  animation: lds-ellipsis2 0.6s infinite;
}
.lds-ellipsis div:nth-child(3) {
  left: 32px;
  animation: lds-ellipsis2 0.6s infinite;
}
.lds-ellipsis div:nth-child(4) {
  left: 56px;
  animation: lds-ellipsis3 0.6s infinite;
}
@keyframes lds-ellipsis1 {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}
@keyframes lds-ellipsis3 {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0);
  }
}
@keyframes lds-ellipsis2 {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(24px, 0);
  }
}
</style>
